import { useState } from "react";
import { Upload, Trash2, FileText, Image as ImageIcon, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { useSite } from "../../context/SiteContext";
import { viewDataUrl } from "../../lib/file";

const inputCls = "w-full bg-white border border-line focus:border-gold outline-none px-4 py-2.5 rounded-sm text-sm focus:ring-2 focus:ring-gold/30";
const labelCls = "block text-xs uppercase tracking-widest text-forest/60 mb-1.5";

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function ProspectusManager() {
  const { prospectus, reload } = useSite();
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({ title: "", category: "" });
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const openNew = () => { setForm({ title: "", category: "" }); setFile(null); setModal(true); };

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 12 * 1024 * 1024) { toast.error("File too large (max 12MB)"); e.target.value = ""; return; }
    setFile(f);
    if (!form.title) setForm((prev) => ({ ...prev, title: f.name.replace(/\.[^.]+$/, "") }));
  };

  const save = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Please choose a PDF or image file"); return; }
    setSaving(true);
    try {
      const dataUrl = await fileToDataUrl(file);
      const type = file.type.includes("pdf") ? "pdf" : file.type.startsWith("image/") ? "image" : "file";
      await api.create("prospectus", { title: form.title || file.name, category: form.category || "General", file: dataUrl, file_name: file.name, file_type: type });
      toast.success("Prospectus uploaded");
      setModal(false);
      await reload();
    } catch { toast.error("Upload failed"); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    try { await api.remove("prospectus", id); toast.success("Deleted"); await reload(); }
    catch { toast.error("Failed to delete"); }
  };

  return (
    <div data-testid="prospectus-manager">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <div>
          <h2 className="font-serif text-2xl text-forest">{prospectus.length} Prospectus File{prospectus.length !== 1 ? "s" : ""}</h2>
          <p className="text-muted text-sm mt-1">Upload PDF or image prospectus/brochure files. Students can view and download them.</p>
        </div>
        <button data-testid="add-prospectus-btn" onClick={openNew} className="inline-flex items-center gap-2 bg-gold text-forest px-6 py-3 rounded-full text-sm uppercase tracking-widest hover:-translate-y-0.5 transition-transform">
          <Upload size={16} /> Upload Prospectus
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {prospectus.map((s) => (
          <div key={s.id} data-testid="prospectus-admin-item" className="bg-white border border-line rounded-sm p-5 flex flex-col">
            <div className="flex items-start gap-3">
              <span className="w-10 h-10 rounded-sm bg-forest/5 text-forest flex items-center justify-center shrink-0">
                {s.file_type === "image" ? <ImageIcon size={18} /> : <FileText size={18} />}
              </span>
              <div className="min-w-0">
                <h3 className="font-serif text-lg text-forest leading-tight">{s.title}</h3>
                <span className="text-[10px] uppercase tracking-widest text-gold">{s.category}</span>
              </div>
            </div>
            <p className="text-xs text-muted mt-3 truncate">{s.file_name}</p>
            <div className="flex items-center gap-2 mt-4">
              <button type="button" data-testid="preview-prospectus-btn" onClick={() => viewDataUrl(s.file)} className="text-xs text-forest underline">Preview</button>
              <button data-testid="delete-prospectus-btn" onClick={() => remove(s.id)} className="ml-auto inline-flex items-center gap-1.5 text-xs text-destructive hover:underline">
                <Trash2 size={13} /> Delete
              </button>
            </div>
          </div>
        ))}
        {prospectus.length === 0 && <p className="p-8 text-muted col-span-full">No prospectus files yet. Upload one above.</p>}
      </div>

      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-forest-900/60 backdrop-blur-sm" onClick={() => setModal(false)}>
          <form data-testid="prospectus-form" onClick={(e) => e.stopPropagation()} onSubmit={save} className="bg-bone w-full max-w-lg rounded-sm border border-line p-8">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-serif text-3xl text-forest">Upload Prospectus</h3>
              <button type="button" onClick={() => setModal(false)} className="text-muted hover:text-forest"><X size={22} /></button>
            </div>
            <div className="mb-5">
              <label className={labelCls}>Title</label>
              <input data-testid="prospectus-field-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required className={inputCls} placeholder="e.g. College Prospectus 2026-27" />
            </div>
            <div className="mb-5">
              <label className={labelCls}>Category</label>
              <input data-testid="prospectus-field-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={inputCls} placeholder="e.g. Admission, General" />
            </div>
            <div className="mb-6">
              <label className={labelCls}>File (PDF or image, max 12MB)</label>
              <input data-testid="prospectus-field-file" type="file" accept=".pdf,image/*" onChange={onPick} className="block w-full text-sm text-muted file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:bg-forest file:text-bone file:text-xs file:uppercase file:tracking-widest" />
              {file && <p className="text-xs text-forest mt-2">{file.name}</p>}
            </div>
            <button data-testid="save-prospectus-btn" type="submit" disabled={saving} className="w-full bg-forest text-bone py-4 rounded-full text-sm uppercase tracking-widest hover:-translate-y-0.5 transition-transform disabled:opacity-60">
              {saving ? "Uploading…" : "Upload"}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
